import React from "react";
import { AlertCircle, ExternalLink } from "lucide-react";

/**
 * DynamicForm Component
 * Renders form fields dynamically based on JSON Schema specification
 * Used for connector configuration forms
 */
const DynamicForm = ({ spec, values, onChange, errors = {} }) => {
  if (!spec || !spec.properties) {
    return (
      <div className="text-center py-8 text-gray-500">
        No configuration fields available
      </div>
    );
  }

  const properties = spec.properties;
  const required = spec.required || [];

  // Sort properties by order if specified
  const sortedProperties = Object.entries(properties).sort((a, b) => {
    const orderA = a[1].order ?? 999;
    const orderB = b[1].order ?? 999;
    return orderA - orderB;
  });

  const renderField = (fieldName, fieldSpec) => {
    const isRequired = required.includes(fieldName);

    // Calculate default value for date fields
    let defaultValue = values[fieldName] || fieldSpec.default || "";

    // If it's a date field and no value is set, default to 1 year ago
    if (
      fieldSpec.format === "date" &&
      !defaultValue &&
      fieldName === "date_ranges_start_date"
    ) {
      const oneYearAgo = new Date();
      oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
      defaultValue = oneYearAgo.toISOString().split("T")[0];

      // Set the default value immediately
      if (!values[fieldName]) {
        onChange(fieldName, defaultValue);
      }
    }

    const value = defaultValue;
    const error = errors[fieldName];

    const handleChange = (newValue) => {
      onChange(fieldName, newValue);
    };

    const commonClasses = `w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
      error ? "border-red-500" : "border-gray-300"
    }`;

    switch (fieldSpec.type) {
      case "string":
        if (fieldSpec.format === "date") {
          return (
            <input
              type="date"
              value={value}
              onChange={(e) => handleChange(e.target.value)}
              className={commonClasses}
              placeholder={fieldSpec.examples?.[0] || ""}
              pattern={fieldSpec.pattern}
            />
          );
        }
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => handleChange(e.target.value)}
            className={commonClasses}
            placeholder={fieldSpec.examples?.[0] || fieldSpec.description}
          />
        );

      case "integer":
      case "number":
        return (
          <input
            type="number"
            value={value}
            onChange={(e) => handleChange(parseInt(e.target.value) || "")}
            className={commonClasses}
            min={fieldSpec.minimum}
            max={fieldSpec.maximum}
            placeholder={fieldSpec.default?.toString() || ""}
          />
        );

      case "boolean":
        return (
          <div className="flex items-center">
            <input
              type="checkbox"
              checked={value || false}
              onChange={(e) => handleChange(e.target.checked)}
              className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
            />
            <label className="ml-2 text-sm text-gray-600">
              {fieldSpec.description}
            </label>
          </div>
        );

      case "array":
        // Handle array of strings (like property_ids)
        if (fieldSpec.items?.type === "string") {
          const arrayValue = Array.isArray(value) ? value : [];
          return (
            <div className="space-y-2">
              {arrayValue.map((item, index) => (
                <div key={index} className="flex gap-2">
                  <input
                    type="text"
                    value={item}
                    onChange={(e) => {
                      const newArray = [...arrayValue];
                      newArray[index] = e.target.value;
                      handleChange(newArray);
                    }}
                    className={commonClasses}
                    placeholder={`Item ${index + 1}`}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const newArray = arrayValue.filter((_, i) => i !== index);
                      handleChange(newArray.length > 0 ? newArray : []);
                    }}
                    className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg"
                  >
                    Remove
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => handleChange([...arrayValue, ""])}
                className="px-4 py-2 text-purple-600 hover:bg-purple-50 rounded-lg"
              >
                + Add {fieldSpec.title || "Item"}
              </button>
            </div>
          );
        }
        return (
          <textarea
            value={JSON.stringify(value, null, 2)}
            onChange={(e) => {
              try {
                handleChange(JSON.parse(e.target.value));
              } catch (err) {
                // Invalid JSON, keep as string
              }
            }}
            className={commonClasses}
            rows={4}
            placeholder="Enter JSON array"
          />
        );

      case "object":
        return (
          <textarea
            value={JSON.stringify(value, null, 2)}
            onChange={(e) => {
              try {
                handleChange(JSON.parse(e.target.value));
              } catch (err) {
                // Invalid JSON, keep as string
              }
            }}
            className={commonClasses}
            rows={4}
            placeholder="Enter JSON object"
          />
        );

      default:
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => handleChange(e.target.value)}
            className={commonClasses}
          />
        );
    }
  };

  return (
    <div className="space-y-6">
      {sortedProperties.map(([fieldName, fieldSpec]) => (
        <div key={fieldName} className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="block text-sm font-medium text-gray-700">
              {fieldSpec.title || fieldName}
              {required.includes(fieldName) && (
                <span className="text-red-500 ml-1">*</span>
              )}
            </label>

            {fieldSpec.help_url && (
              <a
                href={fieldSpec.help_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
              >
                How to find this?
                <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>

          {fieldSpec.description && (
            <p className="text-sm text-gray-500">{fieldSpec.description}</p>
          )}

          {renderField(fieldName, fieldSpec)}

          {errors[fieldName] && (
            <div className="flex items-center gap-2 text-red-600 text-sm">
              <AlertCircle className="w-4 h-4" />
              <span>{errors[fieldName]}</span>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default DynamicForm;
